import Debug from 'debug'
import short from 'short-uuid'
import { Vue, Component, Prop, Watch, Emit } from "vue-property-decorator";
import { resolve } from "@/app/container";
import {
  IncludeComponentsService,
  IncludeComponentsServiceSymbol
} from "@/app/presenter/includer/include-components-service";
import { initTermHints } from "@/app/shared/utils/term-utils";
import { moveFocusToElement } from "@/app/accessibility/accessibility-utils";
import { ChildComponents } from "@/app/shared/utils/child-components";
import {
  TabsState,
  TabsContent
} from "./tabs-interfaces";
import * as Api from "@/app/interaction/api";

const debug = Debug('tabs-component-base')

interface Tab {
  content: string;
  touched: boolean;
}

interface Tabs { 
  [label: string]: Tab;
}

@Component
export default class TabsComponentBase extends Vue
  implements Api.InteractiveComponent<TabsState, TabsContent> {

  private htmlId: string = short.generate()

  @Prop({
    required: true
  })
  public meta: Api.InteractionComponentMeta;

  @Prop({
    required: true
  })
  public content: TabsContent;

  @Prop({
    required: true
  })
  public state: TabsState | null;

  protected internalTabs: Tabs = {};

  protected selectedIndex: number = 0;

  public created() {

    debug('created', this.state)
      
    let index = 0;
    this.selectedIndex = this.state ? this.state.selectedIndex : 0;

    for (let i in this.content.tabs) {
      this.internalTabs[i] = {
        content: this.content.tabs[i],
        touched: this.state ? this.state.touched[index] : index++ === this.selectedIndex,
      };
    }

    // this.emitInteractionStateChanged();
    // this.emitInteractionResultChanged();
  }

  public mounted() {
    this.loadTabContent(this.selectedIndex)
  }

  private includeComponentsService: IncludeComponentsService = resolve<
    IncludeComponentsService
  >(IncludeComponentsServiceSymbol);

  private childComponents: ChildComponents = new ChildComponents(
    "tabs-component"
  );

  // private eventStream: EventStream = resolve<EventStream>(EventStreamSymbol);

  protected setSelectedIndex(value: number): void {
    this.selectedIndex = value;
  }

  protected getTabId(index: number): string {
    return `${this.htmlId}-step-${index}-tab`;
  }

  protected getTabindex(index: number): 0 | -1 {
    if (this.isSelected(index)) {
      return 0;
    }

    return -1;
  }

  protected isSelected(index: number): boolean {
    return this.selectedIndex === index;
  }

  protected isTouched(key: string): boolean {
    // REVIEW: now the label is used as key, this is suboptimal, it does not allow duplicated labels and can cause problems
    let tab = this.internalTabs[key]
    if (!tab) { return false }

    return tab.touched
  }

  protected moveToTab(index: number, step: 1 | -1): void {
    const tabsCount: number = Object.keys(this.internalTabs).length;
    this.selectedIndex = moveFocusToElement(
      index,
      step,
      tabsCount,
      (selectedIndex: number) => {
        return `#${this.getTabId(selectedIndex)}`;
      }
    );
  }

  @Watch("selectedIndex")
  protected async onSelectedIndexChanged(newValue: number) {

    debug('onSelectedIndexChanged', newValue)
      
    await this.loadTabContent(newValue)

    const tab: Tab = Object.values(this.internalTabs)[newValue];

    let progressChanged = !tab.touched
    tab.touched = true;

    this.emitInteractionStateChanged();
    if (progressChanged) {
      this.emitInteractionResultChanged();
    }

    // If eTranslation is active, translate the newly-visible tab content.
    // The widget can only translate DOM-present text, and inactive tabs
    // are removed via v-if, so we trigger translation after the content
    // has been inserted into the DOM by loadTabContent().
    const wt = (window as any).$wt;
    if (wt?.etrans?.toLanguage && wt.etrans.translated) {
      this.$nextTick(() => {
        wt.etrans.translate(
          `#step-${newValue} .tab-content-description`,
          wt.etrans.toLanguage
        );
      });
    }
  }

  public beforeDestroy(): void {
    this.childComponents.destroy();
  }

  // private dispatchInteractionEvent(index: number): void {
  //   this.eventStream.dispatch({
  //     name: SectionInteractedEventName,
  //     payload: {
  //       interaction: {
  //         id: this.content.tabId,
  //         type: TAB_TYPE,
  //         index
  //       }
  //     }
  //   } as SectionInteractionEvent);
  // }

  @Emit("interaction-result-changed")
  public emitInteractionResultChanged() {
    let touchedItems = 0;

    for (let i in this.internalTabs) {
      if (this.internalTabs[i].touched) {
        touchedItems++;
      }
    }

    const progress = parseFloat(
      (touchedItems / Object.keys(this.internalTabs).length).toFixed(2)
    );

    const result: Api.Result = {
      progress,
      status: progress < 1 ? 'incomplete' : 'completed'
    }
    return result
  }

  @Emit("interaction-state-changed")
  public emitInteractionStateChanged() {
    return {
      selectedIndex: this.selectedIndex,
      touched: Object.keys(this.internalTabs).map(
        index => this.internalTabs[index].touched || false
      )
    };
  }
  
  protected async loadTabContent(tabIndex: number) {

    const tab: Tab = Object.values(this.internalTabs)[tabIndex];
    const $parsedContent: JQuery = $(tab.content);
    // TODO: cancel
    const components = await this.includeComponentsService.includeComponents(
      $parsedContent,
      { cancel: false }
    );
    this.childComponents.destroy();
    this.childComponents.register(components);

    setTimeout(() => initTermHints(), 20);

    this.$forceUpdate();
  }

}
